import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

// Cloudflare Turnstile widget (explicit render). DORMANT until
// REACT_APP_TURNSTILE_SITE_KEY is set: getToken() resolves null so callers work unchanged.
// Requires the Turnstile script in public/index.html (see SECURITY_HARDENING.md).
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const TurnstileChallenge = forwardRef(function TurnstileChallenge({ action }, ref) {
  const host = useRef(null);
  const id = useRef(null);
  const pending = useRef(null);
  const chain = useRef(Promise.resolve());
  const [error, setError] = useState("");

  useImperativeHandle(
    ref,
    () => ({
      getToken: () => {
        // Serialize execute() calls so rapid, back-to-back requests (e.g. several
        // voice chunks in one reply) each receive a fresh single-use token.
        const run = () =>
          new Promise((resolve) => {
            if (!SITE_KEY || id.current == null || !window.turnstile) return resolve(null);
            pending.current = { resolve };
            setError("");
            window.turnstile.execute(id.current);
          });
        chain.current = chain.current.then(run, run);
        return chain.current;
      },
      reset: () => id.current != null && window.turnstile?.reset(id.current),
    }),
    []
  );

  useEffect(() => {
    if (!SITE_KEY) return;
    let timer;
    const mount = () => {
      if (!host.current || !window.turnstile || id.current != null) return;
      id.current = window.turnstile.render(host.current, {
        sitekey: SITE_KEY,
        execution: "execute",
        action,
        callback: (token) => {
          pending.current?.resolve(token);
          pending.current = null;
        },
        "error-callback": () => {
          setError("Verification failed, please try again.");
          pending.current?.resolve(null);
          pending.current = null;
        },
        "expired-callback": () => {
          pending.current?.resolve(null);
          pending.current = null;
        },
      });
    };
    if (window.turnstile) mount();
    else timer = setInterval(() => window.turnstile && (clearInterval(timer), mount()), 50);
    return () => {
      clearInterval(timer);
      if (id.current != null) window.turnstile?.remove(id.current);
      id.current = null;
    };
  }, [action]);

  if (!SITE_KEY) return null;
  return (
    <div ref={host} data-testid={`turnstile-${action}`}>
      {error && <p role="alert" className="text-xs text-red-500">{error}</p>}
    </div>
  );
});

export default TurnstileChallenge;
